import styled from 'styled-components';
import classNames from 'classnames';
import {Menu, MenuButton, MenuItems, MenuLink, MenuPopover, useMenuButtonContext} from '@reach/menu-button';
import '@reach/menu-button/styles.css';
import React, {useEffect} from 'react';
import {Link} from '../../Link';
import type {ExtendedBlogCategories} from '../../../types/blogCategories';
import {useLocalizedUrl} from '../../../hooks/useLocalizedUrl';
import {FormattedMessage} from 'react-intl';

type StyledMenuButtonProps = {
  // the children prop is necessary to avoid the collision between children being optional vs mandatory in the types
  children: React.ReactNode;
  className?: unknown;
};

const StyledMenuButton = styled(MenuButton)<StyledMenuButtonProps>`
  padding: 0;
  height: 100%;
  background: none;
  z-index: 99;
  span {
    font-family: Open Sans, sans-serif !important;
  }

  span,
  svg {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  :hover,
  :focus {
    span,
    svg {
      color: #1493be !important;
    }
  }
`;

const StyledMenuLink = styled(MenuLink)`
  &[data-selected] {
    p {
      color: #1493be !important;
    }
  }
`;

type Props = {
  currentPathname: string;
  categories: ExtendedBlogCategories;
};

const PopupWrapper = styled.div`
  top: 120px;
`;

// can be placed anywhere inside a Reach Menu
const MenuScrollPrevention: React.FC = () => {
  const {isExpanded} = useMenuButtonContext();

  useEffect(() => {
    const initial = document.body.style.overflow;
    document.body.style.overflow = isExpanded ? 'hidden' : initial;
    return () => {
      document.body.style.overflow = initial;
    };
  }, [isExpanded]);

  return null;
};

export const BlogMenu: React.FC<Props> = props => {
  const categories = props.categories;
  const localize = useLocalizedUrl();

  const linkClasses = classNames(
    'md:hidden w-full h-full p-5',
    'font-body text-black hover:text-primary focus:text-primary text-base tracking-tight',
    'transition-all ease-in-out duration-300 cursor-pointer',
    'border-none outline-none'
  );
  return (
    <Menu>
      {({isExpanded}) => (
        <div className='bg-grey-bright flex fixed w-full z-9 top-70px'>
          <MenuScrollPrevention />
          <div className='container max-w-screen-xl hidden md:block justify-center'>
            <nav>
              <ul className='flex justify-between'>
                {categories.map(entry => (
                  // we use title in case of Overview which has slug === null
                  <li key={entry.slug || entry.title}>
                    <Link
                      className={classNames(
                        'font-body text-black hover:text-primary text-sm xl:text-base tracking-tight',
                        'transition-all ease-in-out duration-300 cursor-pointer',
                        {
                          'text-primary': entry.selected
                        }
                      )}
                      to={entry.slug === null ? localize('/blog/') : localize(`/blog/${entry.slug}/`)}
                    >
                      {entry.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <StyledMenuButton className={linkClasses}>
            <span
              className={classNames('font-body mr-1', {
                'text-primary': isExpanded,
                'text-black hover:text-primary': !isExpanded
              })}
            >
              <FormattedMessage id='blogPost.showCategories' />
            </span>
            <svg
              width='9px'
              height='6px'
              viewBox='0 0 9 6'
              className={classNames('transition-all duration-200 ease-in-out', {
                'text-primary rotate-180': isExpanded,
                'text-grey hover:text-primarty': !isExpanded
              })}
            >
              <polyline
                transform='translate(4.500000, 3.000000) rotate(-90.000000) translate(-4.500000, -3.000000) translate(3.000000, -0.000000)'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                fill='none'
                points='3 6 0 3 3 0'
              ></polyline>
            </svg>
          </StyledMenuButton>
          <MenuPopover>
            <PopupWrapper className='fixed w-full h-full left-0 transition-all duration-300 ease-in-out bg-grey-bright flex justify-center'>
              <MenuItems className='w-full border-none bg-white'>
                {categories.map(entry => (
                  // using the wrapping div since the default MenuLink comes with it
                  // this should be cleaned up with the redesign
                  // we use title in case of Overview which has slug === null
                  <div key={entry.slug || entry.title} role='none'>
                    <StyledMenuLink
                      forwardedAs={Link}
                      to={entry.slug === null ? localize('/blog/') : localize(`/blog/${entry.slug}/`)}
                      className='bg-transparent text-black p-0 py-3'
                    >
                      <p className='ml-3 mt-1 mb-0 whitespace-normal font-body font-semibold text-grey text-base'>{entry.title}</p>
                    </StyledMenuLink>
                  </div>
                ))}
              </MenuItems>
            </PopupWrapper>
          </MenuPopover>
        </div>
      )}
    </Menu>
  );
};
